import React, { useCallback, useEffect, useState } from 'react';
import {
  KNOWLEDGE_TYPES,
  KNOWLEDGE_ITEM_PREVIEW,
  EVENTS,
  ROUTES,
  INTERNAL_ROUTING,
  FAVORITES_ENTITIES,
  ISSUE_REPORT_ORIGIN,
} from 'shared/constants';
import { TableOfContents } from '../components/table-of-content';
import { notification } from 'antd';
import { useConversion } from '../smart-format-conversion.context';
import { useSelector } from 'react-redux';
import { getOwnersByIDs } from 'shared/store/selectors/owners.selector';
import analyticsService from '../../../helpers/analytics.service';
import styles from '../knowledge-content.module.scss';
import { ShareBoxComponent } from '../../share';
import { KnowledgeUploadFileComponent } from '../knowledge-upload-file';
import { LabelsList } from '../../labels';
import { KnowledgeIssueReport } from '../../issue-report';
import { useUpdatedByInfo } from 'shared/hooks/knowledge.hooks';
import { usePageTitle } from 'src/components/app/use-page-title';
import { FavoritesButton } from 'src/components/favorites-button';
import { DownloadKnowledgeButton } from 'src/components/download-knowledge-button';
import { BackTop, Button } from 'src/components/design-system';
import { KnowledgeViewLayout } from '../layout/knowledge-view-layout';
import { KnowledgeViewers } from '../components/knowledge-viewers/knowledge-viewers.component';
import {
  SmartFormatToggle,
  KnowledgeTags,
  RenderKeywords,
  RenderSuggestKeywords,
  RevisionLog,
} from './components';
import { AttachmentsSection } from './components/attachments-section';
import { RenderSection } from '../components';
import { RenderBack } from '../components/render-back';
import { useDateFormatter } from 'shared/hooks/dates.hooks';
import { useVettedOrProvidedLang } from 'shared/hooks/lang.hooks';
import { Contributors } from '../components/contributors';
import { KnowledgeComments, CommentsHeader } from '../components/knowledge-comments';
import {
  getItemCategory,
  getLastEditedBy,
  getKnowledgeParentsByItemIdWithWorkspace,
  haveEditPermissions,
} from 'shared/store/selectors/knowledge.selector';
import { Breadcrumbs } from 'src/components/breadcrumbs';
import { canImportFromKnowledgeHub } from 'shared/store/selectors/iam.selector';
import { Tooltip } from 'src/components/core';
import { getLang } from 'shared/store/selectors/lang.selector';
import { KnowledgeCommentsProvider } from '../components/knowledge-comments/hooks';
import { useDocumentEditor } from 'src/components/document-editor/hooks/use-document-editor';
import { getFileExtension } from 'shared/utils';
import {
  isExternalSyncReadOnly,
  getFieldVisibility,
  KNOWLEDGE_VISIBILITY_FIELDS,
} from 'shared/utils/knowledge.utils';
import { ShareToHub } from 'src/components/share-to-hub';
import { useUpdateKnowledgeItem } from './hooks/use-update-knowledge-item.hooks';
import { useContentPreview } from 'src/pages/application/knowledge-content/hooks';
import hubServiceApi from 'shared/api/hub.service.api';
import { Owners } from 'src/components/common/owners';
import { useNavigate } from 'react-router-dom';
import { KnowledgeDeletePopup } from 'src/components/knowledge-content/knowledge-content-editor';
import { KnowledgeMovePopup } from 'src/pages/application/knowledge/components/knowledge-row/knowledgeMove';
import { openInNewTab } from '../../../utils/external-link-provider';
import { handleDownloadOriginal } from 'src/utils/getOriginalFileLink';
import { attachmentAnalytics } from 'src/helpers/attachment-analytics';
import knowledgeServiceApi from 'shared/api/knowledge.service.api';
import { HubContentBanner } from 'src/pages/application/knowledge-hub/components/hub-content-banner';

const ASSETS_URL_REGEX =
  /https?:\/\/assets\.(dev\.|staging\.)?(app|eu|local)\.(headtotoe\.io|c8\.health|c8health\.com)(:\d+)?\/|http:\/\/localhost:45(00|55)\//;

const LINKED_IN_INITIAL_COUNT = 1;

const KnowledgeContentPreviewComponent = React.memo(function KnowledgeContentPreviewComponent({
  knowledge: initialKnowledge = {
    title: '',
    subtitle: '',
    thumbnail: '',
    ownerIds: [],
    updatedAt: '',
    fileUpdatedAt: '',
    expiresAt: '',
    keywords: [],
    tags: {},
    content: '',
    link: '',
  },
  lang = {},
  langRevision = {},
  linked = false,
  close = () => {},
  attachmentsLoading = false,
}) {
  const { isHub, item, analyticsTriggers } = useContentPreview();
  let knowledge = initialKnowledge;
  let basePath = ROUTES.KNOWLEDGE_ITEMS;
  let organization = null;

  if (isHub) {
    knowledge = item;
    basePath = ROUTES.KNOWLEDGE_HUB_ITEMS;
    organization = item.organization;
  }
  const {
    id,
    title,
    subtitle,
    ownerIds = [],
    fileUpdatedAt,
    expiresAt,
    keywords,
    tags,
    content,
    link,
    captionsUrl,
    type,
    labels,
    updatedBy,
    metadata,
    options,
    contributors = [],
    workspaceId,
    hubInfo,
    revisionLog,
    attachments,
  } = knowledge;

  let owners = useSelector(getOwnersByIDs(ownerIds));
  const navigate = useNavigate();
  const hasEditPermission = useSelector(state => haveEditPermissions(state, { ownerIds }));
  const updatedAt = revisionLog?.[0]?.updatedAt ?? knowledge.updatedAt;
  const itemCategory = useSelector(state => getItemCategory(state, { id: knowledge.id }));
  const { paths: parents } = useSelector(state =>
    getKnowledgeParentsByItemIdWithWorkspace(state.knowledge, state.workspace, {
      id: knowledge.id,
    }),
  );
  const locatedIn = parents.find(p => !p.linked);
  const linkedIn = parents.filter(p => p.linked);
  const [showAllLinkedIn, setShowAllLinkedIn] = useState(false);
  const visibleLinkedIn = showAllLinkedIn ? linkedIn : linkedIn.slice(0, LINKED_IN_INITIAL_COUNT);
  const [smartFormatMode, setSmartFormatMode] = React.useState(true);
  const [convertFile, setConvertFile] = React.useState(false);
  const { canRenderInEditor } = useDocumentEditor(link, type);
  const [showIssueReportPopup, setShowIssueReportPopup] = React.useState(false);
  const [showLog, setShowLog] = React.useState(false);

  const [deleteKnowledgeItem, setDeleteKnowledgeItem] = React.useState(null);
  const [moveKnowledgeItem, setMoveKnowledgeItem] = React.useState(null);

  const [notificationApi, contextHolder] = notification.useNotification();
  const { formatLongDate } = useDateFormatter();
  const vettedAtDateLang = useVettedOrProvidedLang(lang.VALIDATED_AT_DATE);

  const actionTooltipLang = useSelector(getLang('ACTION_BUTTON_TOOLTIPS'));
  const canImport = useSelector(canImportFromKnowledgeHub);

  const allowedToEdit = !linked && !metadata?.lastSyncAt && !isHub;
  // Synced flag can be on the item or its parent category — check both.
  const isExternalSync =
    isExternalSyncReadOnly(options) || isExternalSyncReadOnly(itemCategory?.options);
  const canEditLabels = isExternalSync && !linked && !isHub;
  const shouldShowField = getFieldVisibility(knowledge, itemCategory);

  const isContent = type === KNOWLEDGE_TYPES.CONTENT;
  const isInternalPDF = type === KNOWLEDGE_TYPES.PDF && !!link?.match(ASSETS_URL_REGEX)?.length;

  const validatedOnDate = formatLongDate(fileUpdatedAt || updatedAt);
  const updatedOnDate = formatLongDate(updatedAt);

  const [originalFormat, setOriginalFormat] = useState('pdf');

  const { updateSubtitle } = usePageTitle();

  useEffect(() => {
    updateSubtitle(title);
  }, [title, updateSubtitle]);

  // Get updated by info - if updatedBy is not available, get the last revision log
  let updatedByInfo = useSelector(state => getLastEditedBy(state, knowledge));

  if (isHub) {
    updatedByInfo = item.updatedByUser;
    owners = item?.owners ?? [item.owner];
  }

  const { updatedByName, updatedByEmail, isC8User } = useUpdatedByInfo({
    updatedBy: updatedByInfo,
  });

  const updatedByUser = isC8User
    ? { firstName: updatedByName, email: updatedByEmail }
    : updatedByInfo;

  const openNotification = useCallback(
    ({ message, type = 'success' }) => {
      notificationApi[type]({
        message,
        placement: 'bottomRight',
      });
    },
    [notificationApi],
  );

  const { setFieldValue } = useUpdateKnowledgeItem({
    item: knowledge,
    openNotification: openNotification,
  });

  useEffect(() => {
    // scroll to top when revision log is opened
    if (showLog) {
      window.scroll(0, 0);
    }
  }, [showLog]);

  useEffect(() => {
    const fetchOriginalFormat = async () => {
      if (!link || type === KNOWLEDGE_TYPES.EXTERNAL_LINK) return;
      try {
        const fileId = link.split('/').pop().split('.').shift();
        const res = await knowledgeServiceApi.getOriginalFile(fileId);
        if (res.data?.format) {
          setOriginalFormat(res.data.format.toLowerCase());
        }
      } catch (err) {
        console.error(' Failed to fetch original file format:', err);
      }
    };
    fetchOriginalFormat();
  }, [link, type]);

  const renderLabels = useCallback(() => {
    if (!labels || labels?.length === 0) return null;

    return (
      <RenderSection header={lang.LABELS}>
        <div className={styles.keywords}>
          <LabelsList labels={labels} />
        </div>
      </RenderSection>
    );
  }, [labels, lang]);

  const toggleSmartFormat = useCallback(
    state => {
      analyticsService.track(EVENTS.KNOWLEDGE_ITEMS.TOGGLE_SMART_FORMAT, {
        id: knowledge?.id,
        title: knowledge?.title,
        type: knowledge.type,
        smartFormat: state,
      });

      setSmartFormatMode(state);
    },
    [knowledge],
  );

  const renderToggleFormat = () => {
    if (!isContent || isHub) {
      return null;
    }
    return (
      <RenderSection>
        <SmartFormatToggle {...{ smartFormatMode, toggleSmartFormat, link }} />
      </RenderSection>
    );
  };

  const onOpenAttachment = useCallback(
    attachment => attachmentAnalytics.attachmentOpen({ attachment, knowledgeItemId: id }),
    [id],
  );

  const toggleEdit = () => {
    analyticsService.track(EVENTS.KNOWLEDGE_ITEMS.EDIT_ITEM, {
      id: knowledge?.id,
      title: knowledge?.title,
      ownerIds: knowledge.ownerIds,
      type: knowledge.type,
      ...knowledge.tags,
      is_new_editor: canRenderInEditor,
      original_format: getFileExtension(link) || null,
    });

    navigate(`${ROUTES.KNOWLEDGE_ITEMS_EDIT.replace(':id', id)}`);
  };

  const cloneFromHub = async () => {
    const { data, error } = await hubServiceApi.prepareClone({ id });

    if (error || !data) {
      openNotification({ message: lang.ERROR });
      return;
    }

    // Navigate to edit mode with the conversion data
    navigate(ROUTES.KNOWLEDGE_ITEMS_NEW, {
      state: {
        initialData: {
          ...data,
          title: data.title || data.fileName || knowledge.title,
        },
        isHub: true,
      },
    });

    analyticsTriggers?.onImport({
      id: knowledge?.id,
      title: knowledge?.title,
      type: knowledge.type,
      ownerIds: knowledge.ownerIds,
      ...knowledge.tags,
      sourceOrganizationName: organization?.name,
      sourceOrganizationHealthSystem: organization?.healthSystem,
      sourceOrganizationDepartment: organization?.department,
    });
  };

  const convertFileToSmartFormat = () => {
    if (!isInternalPDF) return;
    setConvertFile(true);
  };

  const closeDirectConversion = () => {
    setConvertFile(false);
  };

  const openIssueReportPopup = () => {
    setShowIssueReportPopup(true);
  };
  const closeIssueReportPopup = () => {
    setShowIssueReportPopup(false);
  };

  const openLog = () => setShowLog(true);

  const closeLog = () => setShowLog(false);

  const showToastMessage = useCallback(
    category => {
      notificationApi.success({
        title: lang.MOVE_ITEM_SUCCESS.replace('{category}', category?.title || 'new binder'),
        placement: 'bottomRight',
      });
    },
    [lang, notificationApi],
  );

  const { isConvertingKnowledge } = useConversion();

  const renderKnowledgeActions = () => {
    const isConverting = isConvertingKnowledge(id);
    return (
      <section className={styles.knowledgeActions} style={isHub ? { borderTop: 'none' } : {}}>
        {(allowedToEdit || canEditLabels) && (
          <>
            <Tooltip
              title={hasEditPermission ? null : actionTooltipLang.NOT_ALLOWED_TO_EDIT}
              placement="top">
              <Button type="primary" onClick={toggleEdit} disabled={!hasEditPermission}>
                {lang.EDIT}
              </Button>
            </Tooltip>
            {allowedToEdit && !isExternalSync && isInternalPDF && hasEditPermission && (
              <Button
                type="primary"
                ghost={!isConverting}
                onClick={convertFileToSmartFormat}
                disabled={isConverting}>
                {isConverting ? lang.CONVERTING : lang.CONVERT}
              </Button>
            )}
          </>
        )}
        {isHub && canImport && (
          <Button type="primary" onClick={cloneFromHub}>
            {lang.IMPORT_FROM_HUB}
          </Button>
        )}
        <Button type="primary" ghost onClick={openIssueReportPopup}>
          {lang.REPORT_PROBLEM}
        </Button>

        {!isHub && hasEditPermission && (
          <ShareToHub
            name="shareToHub"
            value={knowledge?.shared?.isShared}
            setFieldValue={setFieldValue}
            topBorder
          />
        )}
      </section>
    );
  };

  const renderTableOfContents = () => <TableOfContents content={content} />;

  const renderOwner = () => {
    // No C8 vetter to show (provider-owned review cycle): the date is the last file change.
    if (!shouldShowField(KNOWLEDGE_VISIBILITY_FIELDS.OWNERS))
      return vettedAtDateLang.replace('{date}', validatedOnDate);
    if (!owners.length) return null;

    let hubOrganizationSource = null;

    if (organization) {
      hubOrganizationSource = organization.department
        ? ` ${lang.DEPARTMENT} ${organization?.department}, ${organization?.healthSystem} `
        : organization.name;
    }

    return (
      <>
        <div className={styles.spaceRight}>
          <Owners
            initialOwners={owners}
            navigateToOwnerProfile={!isHub}
            title={lang.VALIDATED_BY}
          />
        </div>
        {hubOrganizationSource && <div className={styles.bold}>{hubOrganizationSource}</div>}
        {lang.ON_DATE.replace('{date}', validatedOnDate)}
      </>
    );
  };

  const renderHubInfo = () => {
    if (isHub || !hubInfo) return null;

    const { owners, organization } = hubInfo;

    const hubOrganizationSource = organization.department
      ? ` ${lang.DEPARTMENT} ${organization?.department}, ${organization?.healthSystem} `
      : organization.name;

    return (
      <>
        {' | '}
        <div className={styles.spaceLeft}>
          <Owners initialOwners={owners} navigateToOwnerProfile={false} title={lang.ADAPTED} />
        </div>
        <p className={styles.bold}>, </p>
        <p className={styles.bold}>{hubOrganizationSource}</p>
      </>
    );
  };
  const renderHeader = () => {
    return (
      <div className={styles.contentHeader}>
        <div className={styles.iconsWrapper}>
          {originalFormat && type !== KNOWLEDGE_TYPES.EXTERNAL_LINK && (
            <DownloadKnowledgeButton
              originalFormat={originalFormat}
              onDownloadPDF={() => {
                openInNewTab({ id, title, link, content });
              }}
              onDownloadOriginal={() => handleDownloadOriginal(link)}
            />
          )}
          <ShareBoxComponent
            path={basePath.replace(':id', id)}
            id={id}
            title={title}
            tags={tags}
            type={type}
            entityType={KNOWLEDGE_ITEM_PREVIEW}
            iconClassName={styles.shareIcon}
          />
          {!isHub && (
            <FavoritesButton
              id={id}
              entity={FAVORITES_ENTITIES.KNOWLEDGE}
              className={styles.bookmarkIcon}
              wrapper={styles.bookmarkWrapper}
            />
          )}
        </div>
        <div className={styles.header}>
          <RenderBack close={close} />
          <h1 className={styles.title}>{title}</h1>
        </div>
        {subtitle && <h3 className={styles.subtitle}>{subtitle}</h3>}
        <div className={styles.metadata}>
          {renderOwner()}
          {renderHubInfo()}
          {updatedBy && (
            <>
              {' | '}
              {lang.UPDATED_BY}
              <div className={styles.spaceHorizontal}>
                <Owners initialOwners={[updatedByUser]} navigateToOwnerProfile={!isC8User} />
              </div>
              {lang.ON_DATE.replace('{date}', updatedOnDate)}
            </>
          )}
          <div className={styles.spaceLeft}>
            <Contributors contributors={contributors} subtitleSeparator=" | " />
          </div>
        </div>
      </div>
    );
  };

  const renderRightPanel = () => {
    return (
      <>
        {!isHub && <CommentsHeader />}

        {renderToggleFormat()}

        {renderKnowledgeActions()}

        {updatedBy && (
          <RenderSection header={lang.UPDATED_BY}>
            <div className={styles.updatedBy}>
              <Owners initialOwners={[updatedByUser]} navigateToOwnerProfile={!isC8User} />
              <h4>{lang.ON_DATE.replace('{date}', updatedOnDate)}</h4>
            </div>
          </RenderSection>
        )}

        <KnowledgeTags tags={tags} workspaceId={workspaceId} />

        {locatedIn && (
          <RenderSection header={lang.LOCATED_IN}>
            <Breadcrumbs
              wrapperStyle={styles.locationBreadcrumbs}
              pathParts={locatedIn.pathParts}
              title={locatedIn.title}
              workspaceName={locatedIn.workspaceName}
              initialWorkspaceId={locatedIn.workspaceId}
              hideRoot
              currentItemId={locatedIn.id}
              currentQueryParamKey={INTERNAL_ROUTING.QUERY_PARAMS.KNOWLEDGE}
              sectionLinks
            />
          </RenderSection>
        )}

        {linkedIn.length > 0 && (
          <RenderSection header={lang.LINKED_IN}>
            {visibleLinkedIn.map(category => (
              <Breadcrumbs
                wrapperStyle={styles.locationBreadcrumbs}
                key={category.id}
                pathParts={category.pathParts}
                title={category.title}
                workspaceName={category.workspaceName}
                initialWorkspaceId={category.workspaceId}
                hideRoot
                currentItemId={category.id}
                currentQueryParamKey={INTERNAL_ROUTING.QUERY_PARAMS.KNOWLEDGE}
                sectionLinks
              />
            ))}
            {linkedIn.length > LINKED_IN_INITIAL_COUNT && (
              <Button
                type="link"
                className={styles.showMorePathButton}
                onClick={() => setShowAllLinkedIn(prev => !prev)}>
                {showAllLinkedIn
                  ? lang.SHOW_LESS
                  : lang.AND_COUNT_MORE.replace(
                      '{count}',
                      linkedIn.length - LINKED_IN_INITIAL_COUNT,
                    )}
              </Button>
            )}
          </RenderSection>
        )}

        {renderLabels()}

        <RenderSection header={lang.EXPIRATION_DATE}>
          <p className={styles.content}>
            {!expiresAt ? lang.NEVER_EXPIRES : formatLongDate(expiresAt)}
          </p>
        </RenderSection>

        <RenderKeywords keywords={keywords} />

        {!isHub && !isExternalSync && (
          <RenderSuggestKeywords {...{ id, keywords, openNotification }} />
        )}

        {!isHub && (
          <RenderSection>
            <span>
              {langRevision.VIEW_REVISION}
              <button className={styles.link} onClick={openLog}>
                {langRevision.TITLE}
              </button>
            </span>
          </RenderSection>
        )}

        {!isHub && !isExternalSync && (
          <div className={styles.knowledgeActions}>
            {hasEditPermission && (
              <Button type="primary" onClick={() => setMoveKnowledgeItem(knowledge)}>
                {lang.MOVE_ITEM}
              </Button>
            )}
            {hasEditPermission && (
              <Button danger onClick={() => setDeleteKnowledgeItem(knowledge)}>
                {lang.DELETE_ITEM}
              </Button>
            )}
          </div>
        )}

        <KnowledgeDeletePopup
          open={!!deleteKnowledgeItem}
          entity={deleteKnowledgeItem}
          onDelete={() => {
            setDeleteKnowledgeItem(null);
            close();
          }}
          onFinish={() => setDeleteKnowledgeItem(null)}
        />

        <KnowledgeMovePopup
          isOpen={!!moveKnowledgeItem}
          item={moveKnowledgeItem}
          categoryId={itemCategory?.id || null}
          onFinish={() => setMoveKnowledgeItem(null)}
          showToast={showToastMessage}
        />
      </>
    );
  };

  return (
    <>
      <KnowledgeCommentsProvider knowledgeId={id}>
        <KnowledgeViewLayout
          hideLeftPanel={!(smartFormatMode && isContent)}
          renderLeftPanel={renderTableOfContents}
          renderHeader={renderHeader}
          renderRightPanel={renderRightPanel}
          renderFooter={() => !isHub && <KnowledgeComments />}
          renderTopBanner={() => (isHub ? <HubContentBanner /> : null)}>
          {!showLog && (
            <KnowledgeViewers
              {...{
                id,
                title,
                link,
                captionsUrl,
                content,
                type,
                isContent,
                smartFormatMode,
                ownerIds,
                linked,
                close,
              }}
            />
          )}
          {!showLog && (
            <AttachmentsSection
              attachments={attachments}
              loading={attachmentsLoading}
              onOpen={onOpenAttachment}
            />
          )}
          {showLog && (
            <RevisionLog
              close={close}
              closeLog={closeLog}
              log={knowledge.revisionLog}
              lang={langRevision}
              link={link}
            />
          )}
          <BackTop
            className={styles.backTop}
            type="primary"
            target={() => document.querySelector('#full-width')}
          />
        </KnowledgeViewLayout>

        <KnowledgeUploadFileComponent
          {...knowledge}
          open={!!convertFile}
          convertPastItemToSmartFormat={convertFile}
          editContent={data => {
            navigate(ROUTES.KNOWLEDGE_ITEMS_EDIT.replace(':id', knowledge.id), {
              state: {
                initialData: {
                  ...data,
                  title:
                    data.title ||
                    data.fileName?.slice(0, data.fileName?.lastIndexOf('.')) ||
                    knowledge.title,
                },
              },
            });
          }}
          close={closeDirectConversion}
          bypassOpen
        />

        <KnowledgeIssueReport
          open={showIssueReportPopup}
          close={closeIssueReportPopup}
          notificationApi={notificationApi}
          itemId={id}
          origin={isHub ? ISSUE_REPORT_ORIGIN.KNOWLEDGE_HUB : ISSUE_REPORT_ORIGIN.KNOWLEDGE_BASE}
        />
      </KnowledgeCommentsProvider>

      {contextHolder}
    </>
  );
});

export { KnowledgeContentPreviewComponent };
